import React, { useMemo } from 'react'

import { useFinancialsTableContext } from '../../components/financials-table'
import { WidgetHeader } from '../earnings-common/WidgetHeader'
import { ESTIMATE_TYPE_LABEL } from '../earnings-common/constants'
import { EarningsEstimateType, EarningsSettings } from '../earnings-common/types'
import { EstimateTypeChipSwitch } from './EstimateTypeChipSwitch'
import LatestRevision from './LatestRevision'
import { EarningRevisionWithDate, EarningsRevisionsByType } from './types'
import { adjustEstimate, formatQuarterName, formatYearName } from './utils'

function getLatestRevision(revisions: EarningRevisionWithDate[]): EarningRevisionWithDate | null {
  return revisions && revisions.length > 0 ? revisions[revisions.length - 1] : null
}

function hasRevisionData(revisionData: EarningsRevisionsByType, estimateType: EarningsEstimateType): boolean {
  return !!(
    Object.keys(revisionData[estimateType].quarters).length || Object.keys(revisionData[estimateType].years).length
  )
}

interface Props {
  revisionData: EarningsRevisionsByType
}

export default function LatestRevisions({ revisionData }: Props) {
  const { settings } = useFinancialsTableContext<EarningsSettings>()

  const estimateType = settings.estimateType

  const data = useMemo(() => {
    const quarters = Object.keys(revisionData[estimateType].quarters)
    const years = Object.keys(revisionData[estimateType].years)
    const lastQuarterKey = quarters[quarters.length - 1]
    const semilastQuarterKey = quarters[quarters.length - 2]
    const lastYearKey = years[years.length - 1]
    const semilastYearKey = years[years.length - 2]
    const hasDataByType = {
      [EarningsEstimateType.eps]: hasRevisionData(revisionData, EarningsEstimateType.eps),
      [EarningsEstimateType.epsGaap]: hasRevisionData(revisionData, EarningsEstimateType.epsGaap),
      [EarningsEstimateType.sales]: hasRevisionData(revisionData, EarningsEstimateType.sales),
    }
    return {
      lastQuarter: getLatestRevision(revisionData[estimateType].quarters[lastQuarterKey]),
      semilastQuarter: getLatestRevision(revisionData[estimateType].quarters[semilastQuarterKey]),
      lastYear: getLatestRevision(revisionData[estimateType].years[lastYearKey]),
      semilastYear: getLatestRevision(revisionData[estimateType].years[semilastYearKey]),
      hasDataByType,
    }
  }, [estimateType, revisionData])

  const hasData = data.lastQuarter || data.semilastQuarter || data.lastYear || data.semilastYear

  return (
    <div>
      <div className="flex justify-between">
        <WidgetHeader className="pl-1.5">Latest Revisions</WidgetHeader>
        <EstimateTypeChipSwitch hasDataByType={data.hasDataByType} label="Estimate" />
      </div>
      {hasData && (
        <div className="flex pt-2.5">
          {data.semilastQuarter && (
            <LatestRevision
              label={formatQuarterName(data.semilastQuarter.fiscalPeriod)}
              estimate={adjustEstimate(data.semilastQuarter.mean, estimateType)}
              upRevisions={data.semilastQuarter.upRevisions}
              downRevisions={data.semilastQuarter.downRevisions}
              revisions={data.semilastQuarter.estimates}
              revisionDate={data.semilastQuarter.date}
            />
          )}
          {data.lastQuarter && (
            <LatestRevision
              label={formatQuarterName(data.lastQuarter.fiscalPeriod)}
              estimate={adjustEstimate(data.lastQuarter.mean, estimateType)}
              upRevisions={data.lastQuarter.upRevisions}
              downRevisions={data.lastQuarter.downRevisions}
              revisions={data.lastQuarter.estimates}
              revisionDate={data.lastQuarter.date}
            />
          )}
          {data.semilastYear && (
            <LatestRevision
              label={formatYearName(data.semilastYear.fiscalPeriod)}
              estimate={adjustEstimate(data.semilastYear.mean, estimateType)}
              upRevisions={data.semilastYear.upRevisions}
              downRevisions={data.semilastYear.downRevisions}
              revisions={data.semilastYear.estimates}
              revisionDate={data.semilastYear.date}
            />
          )}
          {data.lastYear && (
            <LatestRevision
              label={formatYearName(data.lastYear.fiscalPeriod)}
              estimate={adjustEstimate(data.lastYear.mean, estimateType)}
              upRevisions={data.lastYear.upRevisions}
              downRevisions={data.lastYear.downRevisions}
              revisions={data.lastYear.estimates}
              revisionDate={data.lastYear.date}
            />
          )}
        </div>
      )}
      {!hasData && (
        <div className="fv-container flex items-center justify-center pb-1 text-2xs text-muted-2">
          No {ESTIMATE_TYPE_LABEL[estimateType]} data available.
        </div>
      )}
    </div>
  )
}
